import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { boldCellSx, renderCell } from '../../../components/ui/tableTemplate/cellRenderers';
import type { TableColumn } from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { formatDateTime } from '../../../lib/date/formatDateTime';
import { PerimeterBadges } from '../components/PerimeterBadges';
import { ProfileBadge } from '../components/ProfileBadge';
import { UserStatusBadge } from '../components/UserStatusBadge';
import { UserRowActions } from '../components/UserRowActions/UserRowActions';
import type { User } from '../userTypes';

/** Row-action wiring (HOMCO-1366). The actions column appears when `canModify` or `canDelete`. */
export interface UserColumnsActions {
  canModify: boolean;
  canDelete: boolean;
  callerRole: string | null;
  callerUsername: string | null;
  onEdit: (user: User) => void;
  onResetPassword: (user: User) => void;
  onDeactivate: (user: User) => void;
  onReactivate: (user: User) => void;
  onDelete: (user: User) => void;
}

/** Columns of the users list — plain cells use the shared `renderCell` style. */
export function useUserColumns(actions?: UserColumnsActions): TableColumn<User>[] {
  const { t } = useTranslation('users');

  return useMemo<TableColumn<User>[]>(() => {
    const columns: TableColumn<User>[] = [
      {
        key: 'username',
        labelKey: 'users:table.columns.username',
        width: 170,
        filter: { kind: 'text' },
        render: user => renderCell('text', user.username, { t }, boldCellSx),
      },
      {
        key: 'display_name',
        labelKey: 'users:table.columns.displayName',
        width: 190,
        filter: { kind: 'text' },
        render: user => renderCell('text', user.display_name ?? '', { t }, boldCellSx),
      },
      { key: 'email', labelKey: 'users:table.columns.email', width: 240 },
      {
        key: 'profile_name',
        labelKey: 'users:table.columns.profile',
        width: 180,
        render: user =>
          user.profile_name && user.profile_id ? (
            <Link
              to={`/my-ngo/user-rights/${user.profile_id}/edit`}
              onClick={e => e.stopPropagation()}
              style={{ textDecoration: 'none' }}
            >
              <ProfileBadge name={user.profile_name} />
            </Link>
          ) : user.profile_name ? (
            <ProfileBadge name={user.profile_name} />
          ) : null,
      },
      {
        key: 'perimeter_nodes',
        labelKey: 'users:table.columns.perimeter',
        width: 240,
        render: user => <PerimeterBadges user={user} />,
      },
      {
        key: 'is_active',
        labelKey: 'users:table.columns.status',
        width: 120,
        filter: {
          kind: 'list',
          options: ['active', 'inactive'],
          optionLabelPrefix: 'users:table.status',
        },
        render: user => <UserStatusBadge active={user.is_active} />,
      },
      {
        key: 'last_login_at',
        labelKey: 'users:table.columns.lastLogin',
        width: 170,
        render: user =>
          renderCell(
            'text',
            user.last_login_at
              ? formatDateTime(user.last_login_at)
              : t('users:table.lastLogin.never'),
            { t },
          ),
      },
    ];

    if (actions?.canModify || actions?.canDelete) {
      columns.push({
        key: 'id',
        labelKey: '',
        width: 72,
        align: 'center',
        stickyRight: true,
        render: user => (
          <UserRowActions
            user={user}
            canModify={actions.canModify}
            canDelete={actions.canDelete}
            callerRole={actions.callerRole}
            callerUsername={actions.callerUsername}
            onEdit={actions.onEdit}
            onResetPassword={actions.onResetPassword}
            onDeactivate={actions.onDeactivate}
            onReactivate={actions.onReactivate}
            onDelete={actions.onDelete}
          />
        ),
      });
    }

    return columns;
  }, [t, actions]);
}
